import React, { useEffect, useState } from 'react';

import { useWaApi } from '../../auth/helper/api/waApi';

/**
 * Read-only view of the signed-in user's own profile. Nothing identifying is sent with the
 * request - the service resolves the profile from the token's user_id claim.
 */
const WaMyProfilePage = () => {

  const waApi = useWaApi();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchProfile = async () => {
      try {
        const response = await waApi.get(process.env.REACT_APP_GET_MY_WA_USER_PROFILE_ENDPOINT);
        if (cancelled) return;
        const { message, object, httpStatus } = response.data;
        if (httpStatus === 200 && object) {
          setProfile(object);
        } else {
          setError(message || 'No profile is linked to your account.');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Unable to load your profile.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchProfile();
    return () => { cancelled = true; };
  }, [waApi]);

  const initials = (first, last) =>
    `${(first || '').charAt(0)}${(last || '').charAt(0)}`.toUpperCase() || '?';

  const formatDate = (value) => (value ? new Date(value).toLocaleString() : '--');
  const orDash = (value) => (value === null || value === undefined || value === '' ? '--' : value);

  if (loading) return <div className="page-container">Loading your profile...</div>;

  if (error) {
    return (
      <div className="page-container">
        <h1>My User Profile</h1>
        <div className="profile-empty">{error}</div>
      </div>
    );
  }

  const wabaIds = profile.wabaIds ?? [];

  return (
    <div className="page-container">
      <div className="profile-header">
        <div className="profile-avatar">{initials(profile.firstName, profile.lastName)}</div>
        <div className="profile-identity">
          <h1>{[profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.userId}</h1>
          <p className="profile-subtitle">{profile.email}</p>
          <div className="profile-badges">
            <span className="profile-badge profile-badge-role">{orDash(profile.userRole)}</span>
            <span className={`profile-badge ${profile.status ? 'profile-badge-active' : 'profile-badge-inactive'}`}>
              {profile.status ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>

      <section className="profile-section">
        <h3 className="profile-section-title">Account</h3>
        <div className="profile-grid">
          <div className="profile-field">
            <span className="profile-label">Profile Id</span>
            <span className="profile-value">{orDash(profile.id)}</span>
          </div>
          <div className="profile-field">
            <span className="profile-label">User Id</span>
            <span className="profile-value">{orDash(profile.userId)}</span>
          </div>
          <div className="profile-field">
            <span className="profile-label">Tenant Id</span>
            <span className="profile-value">{orDash(profile.tenantId)}</span>
          </div>
          <div className="profile-field">
            <span className="profile-label">User Role</span>
            <span className="profile-value">{orDash(profile.userRole)}</span>
          </div>
        </div>
      </section>

      <section className="profile-section">
        <h3 className="profile-section-title">Personal</h3>
        <div className="profile-grid">
          <div className="profile-field">
            <span className="profile-label">First Name</span>
            <span className="profile-value">{orDash(profile.firstName)}</span>
          </div>
          <div className="profile-field">
            <span className="profile-label">Last Name</span>
            <span className="profile-value">{orDash(profile.lastName)}</span>
          </div>
          <div className="profile-field">
            <span className="profile-label">Email</span>
            <span className="profile-value">{orDash(profile.email)}</span>
          </div>
          <div className="profile-field profile-field-wide">
            <span className="profile-label">Address</span>
            <span className="profile-value">{orDash(profile.address)}</span>
          </div>
        </div>
      </section>

      <section className="profile-section">
        <h3 className="profile-section-title">Organisation</h3>
        <div className="profile-grid">
          <div className="profile-field">
            <span className="profile-label">Org Id</span>
            <span className="profile-value">{orDash(profile.orgId)}</span>
          </div>
          <div className="profile-field">
            <span className="profile-label">Emp Id</span>
            <span className="profile-value">{orDash(profile.empId)}</span>
          </div>
        </div>
      </section>

      <section className="profile-section">
        <h3 className="profile-section-title">
          WABA Accounts <span className="profile-count">{wabaIds.length}</span>
        </h3>
        {wabaIds.length === 0 ? (
          <div className="profile-empty">No WABA account is linked to this profile.</div>
        ) : (
          <div className="profile-chips">
            {wabaIds.map((wabaId) => (
              <span key={wabaId} className="profile-chip">{wabaId}</span>
            ))}
          </div>
        )}
      </section>

      <section className="profile-section">
        <h3 className="profile-section-title">Record</h3>
        <div className="profile-grid">
          <div className="profile-field">
            <span className="profile-label">Created</span>
            <span className="profile-value">{formatDate(profile.createdTimestamp)}</span>
          </div>
          <div className="profile-field">
            <span className="profile-label">Last Updated</span>
            <span className="profile-value">{formatDate(profile.updatedTimestamp)}</span>
          </div>
        </div>
      </section>

      <p className="profile-note">
        This view is read only. Contact an administrator to have your details changed.
      </p>
    </div>
  );
};

export default WaMyProfilePage;
